import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { apiErr } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import WorkerPicker from "@/components/op/WorkerPicker";
import SearchSelect from "@/components/op/SearchSelect";
import OpReceipt from "@/components/op/OpReceipt";
import OpHeader from "@/components/op/OpHeader";

const num = (n) => new Intl.NumberFormat("id-ID").format(n || 0);
const PAGE = 10;

export default function Setoran() {
  const [worker, setWorker] = useState(null);
  const [items, setItems] = useState([{ product_id: "", quantity: "", reject: "", repairable: true, keterangan: "" }]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [page, setPage] = useState(0);
  const idemRef = useRef(crypto.randomUUID());

  useEffect(() => { api.get("/op/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  const setItem = (i, k, v) => setItems((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const bagus = (it) => Math.max(Number(it.quantity || 0) - Number(it.reject || 0), 0);
  const addItem = () => { setItems((a) => [...a, { product_id: "", quantity: "", reject: "", repairable: true, keterangan: "" }]); setPage(Math.floor(items.length / PAGE)); };

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE));
  const curPage = Math.min(page, totalPages - 1);
  const pageIdx = items.map((_, i) => i).slice(curPage * PAGE, curPage * PAGE + PAGE);

  const save = async (print) => {
    if (!worker) return toast.error("Pilih pengepul dulu");
    const v = [];
    for (const it of items) {
      if (!it.product_id || !(Number(it.quantity) > 0)) continue;
      const rj = Number(it.reject || 0);
      if (rj > Number(it.quantity)) return toast.error("Rijek melebihi jumlah setor");
      if (rj > 0 && !it.keterangan.trim()) return toast.error("Keterangan rijek wajib diisi");
      v.push({ product_id: it.product_id, quantity: Number(it.quantity), reject: rj, repairable: it.repairable !== false, keterangan: it.keterangan || null });
    }
    if (!v.length) return toast.error("Isi minimal satu barang setor");
    setSaving(true);
    try {
      const { data } = await api.post("/op/setoran", { worker_id: worker.worker_id, items: v, idempotency_key: idemRef.current });
      toast.success(`Tersimpan ke Ledger · ${data.op_no}`);
      setReceipt(data);
      idemRef.current = crypto.randomUUID();
      setItems([{ product_id: "", quantity: "", reject: "", repairable: true, keterangan: "" }]); setWorker(null); setPage(0);
      if (print) setTimeout(() => window.print(), 400);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const Pager = () => totalPages <= 1 ? null : (
    <div className="flex items-center justify-between rounded-full bg-slate-100 px-2 py-1" data-testid="op-pager">
      <button type="button" disabled={curPage === 0} onClick={() => setPage(curPage - 1)} className="p-1.5 rounded-full disabled:opacity-30 hover:bg-white" data-testid="op-page-prev"><ChevronLeft className="w-4 h-4" /></button>
      <span className="text-xs font-semibold text-slate-600" data-testid="op-page-indicator">Halaman {curPage + 1} / {totalPages}</span>
      <button type="button" disabled={curPage >= totalPages - 1} onClick={() => setPage(curPage + 1)} className="p-1.5 rounded-full disabled:opacity-30 hover:bg-white" data-testid="op-page-next"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );

  return (
    <div className="w-full pb-10" data-testid="op-setoran">
      <OpHeader title="SETORAN" />
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-5">
        <WorkerPicker value={worker} onChange={setWorker} />
        <Pager />
        <div className="space-y-3">
          {pageIdx.map((i) => {
            const it = items[i];
            return (
              <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`op-setor-row-${i}`}>
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6">
                    <Label className="text-[11px]">Barang</Label>
                    <SearchSelect value={it.product_id} onValueChange={(v) => setItem(i, "product_id", v)}
                      options={products.map((p) => ({ value: p.product_id, label: p.product_name }))}
                      placeholder="Pilih / cari barang" testId={`op-setor-product-${i}`} />
                  </div>
                  <div className="col-span-2"><Label className="text-[11px]">Jml Setor</Label><Input type="number" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="h-10" data-testid={`op-setor-qty-${i}`} /></div>
                  <div className="col-span-2"><Label className="text-[11px]">Rijek</Label><Input type="number" value={it.reject} onChange={(e) => setItem(i, "reject", e.target.value)} className="h-10" data-testid={`op-setor-reject-${i}`} /></div>
                  <div className="col-span-1 text-center"><Label className="text-[11px]">Bagus</Label><div className="h-10 flex items-center justify-center font-bold text-emerald-700" data-testid={`op-setor-good-${i}`}>{bagus(it)}</div></div>
                  <div className="col-span-1"><button onClick={() => setItems((a) => a.filter((_, j) => j !== i))} className="p-2 text-rose-500 mt-5" data-testid={`op-setor-del-${i}`}><Trash2 className="w-4 h-4" /></button></div>
                </div>
                {Number(it.reject) > 0 && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex rounded-lg bg-slate-100 p-0.5 text-[11px] font-semibold shrink-0" data-testid={`op-setor-type-${i}`}>
                      <button type="button" onClick={() => setItem(i, "repairable", true)} className={`px-2 py-1 rounded-md ${it.repairable !== false ? "bg-white shadow text-emerald-700" : "text-slate-500"}`} data-testid={`op-setor-rep-${i}`}>Bisa diperbaiki</button>
                      <button type="button" onClick={() => setItem(i, "repairable", false)} className={`px-2 py-1 rounded-md ${it.repairable === false ? "bg-white shadow text-rose-700" : "text-slate-500"}`} data-testid={`op-setor-afkir-${i}`}>Afkir</button>
                    </div>
                    <Input placeholder="Keterangan rijek (wajib)" value={it.keterangan} onChange={(e) => setItem(i, "keterangan", e.target.value)} className="h-9" data-testid={`op-setor-note-${i}`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={addItem} data-testid="op-add-setor"><Plus className="w-4 h-4 mr-1" />Tambah Barang</Button>
        <Pager />
        <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-sm">
          <div className="text-slate-500">Total Setor <b className="text-slate-900">{num(items.reduce((a, x) => a + Number(x.quantity || 0), 0))}</b> · Rijek <b className="text-orange-600">{num(items.reduce((a, x) => a + Number(x.reject || 0), 0))}</b> · Bagus <b className="text-emerald-700">{num(items.reduce((a, x) => a + bagus(x), 0))}</b></div>
          <div className="flex gap-2">
            <Button disabled={saving} onClick={() => save(false)} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="op-save"><Save className="w-4 h-4 mr-1.5" />Simpan</Button>
            <Button disabled={saving} onClick={() => save(true)} variant="outline" className="rounded-full" data-testid="op-save-print">Simpan & Cetak</Button>
          </div>
        </div>
      </div>
      {receipt && <OpReceipt doc={receipt} onClose={() => setReceipt(null)} />}
    </div>
  );
}
